import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import {
  FiPlay, FiImage, FiCheck, FiX, FiZap, FiLayout, FiCode, FiAward,
  FiShield, FiGift, FiChevronDown, FiCheckCircle, FiArrowRight, FiStar
} from 'react-icons/fi';

// Swap these for the real assets (AI-generated video/screenshots). Each
// placeholder is intentionally loud so none ships unnoticed.
const MediaPlaceholder = ({ label, aspect = 'aspect-video', icon = FiPlay }) => (
  <div className={`relative w-full ${aspect} rounded-2xl border-2 border-dashed border-orange-500/25 bg-zinc-900/40 flex flex-col items-center justify-center gap-4 overflow-hidden`}>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-600/10 blur-[100px] rounded-full pointer-events-none"></div>
    <div className="w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center relative z-10">
      <SafeIcon icon={icon} className="text-2xl text-orange-400" />
    </div>
    <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 relative z-10 text-center px-6">{label}</span>
  </div>
);

const FeatureCard = ({ icon, title, desc }) => (
  <div className="p-6 rounded-2xl bg-zinc-900/40 border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300">
    <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4">
      <SafeIcon icon={icon} className="text-orange-400 text-lg" />
    </div>
    <h4 className="text-amber-50 font-semibold mb-1.5">{title}</h4>
    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/5 rounded-xl bg-zinc-900/30 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-amber-50 hover:bg-zinc-900/50 transition-colors"
      >
        {q}
        <motion.div animate={{ rotate: open ? 180 : 0 }} className="flex-shrink-0 text-zinc-500">
          <SafeIcon icon={FiChevronDown} />
        </motion.div>
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-zinc-400 leading-relaxed">{a}</div>
      )}
    </div>
  );
};

const ComparisonRow = ({ label, free, ltd, pro }) => {
  const cell = (v) =>
    v === true ? <SafeIcon icon={FiCheck} className="text-green-400 mx-auto" />
    : v === false ? <SafeIcon icon={FiX} className="text-zinc-700 mx-auto" />
    : <span className="text-xs">{v}</span>;
  return (
    <tr className="border-t border-white/5">
      <td className="py-3 pr-4 text-sm text-zinc-400 text-left">{label}</td>
      <td className="py-3 px-3 text-center text-zinc-400">{cell(free)}</td>
      <td className="py-3 px-3 text-center text-amber-100 bg-orange-500/5">{cell(ltd)}</td>
      <td className="py-3 px-3 text-center text-zinc-400">{cell(pro)}</td>
    </tr>
  );
};

const LifetimeDeal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemState, setRedeemState] = useState(null); // {ok, message}
  const [buying, setBuying] = useState(false);

  const alreadyLifetime = user?.tier === 'beta';

  // Returning from the auth round-trip: restore the code they typed.
  useEffect(() => {
    const saved = localStorage.getItem('mot_ltd_code');
    if (saved && user) {
      setCode(saved);
      localStorage.removeItem('mot_ltd_code');
      document.getElementById('redeem')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [user]);

  const requireAuth = () => {
    if (user) return true;
    if (code.trim()) localStorage.setItem('mot_ltd_code', code.trim());
    navigate('/auth', { state: { from: { pathname: '/ltd' } } });
    return false;
  };

  const handleRedeem = async () => {
    if (!code.trim()) {
      setRedeemState({ ok: false, message: 'Enter your code first.' });
      return;
    }
    if (!requireAuth()) return;

    setRedeeming(true);
    setRedeemState(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('redeem-ltd-code', {
        body: { code: code.trim() },
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });

      if (error) {
        let msg = '';
        try { msg = (await error.context?.json?.())?.error || ''; } catch { /* not json */ }
        throw new Error(msg || 'Redemption failed. Please try again.');
      }
      if (data?.error) throw new Error(data.error);

      setRedeemState({ ok: true, message: data?.message || 'Lifetime access unlocked!' });
    } catch (err) {
      setRedeemState({ ok: false, message: err.message });
    } finally {
      setRedeeming(false);
    }
  };

  const handleBuy = async () => {
    if (!requireAuth()) return;
    setBuying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { planType: 'ltd' },
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });
      if (error || !data?.checkout_url) throw new Error(data?.error || 'Could not start checkout. Please try again.');
      window.location.href = data.checkout_url;
    } catch (err) {
      alert(err.message);
      setBuying(false);
    }
  };

  const scrollToRedeem = () => document.getElementById('redeem')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="relative overflow-hidden">
      {/* Background glow */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-orange-600/10 blur-[130px] pointer-events-none"></div>

      {/* ---------- HERO ---------- */}
      <section className="pt-36 pb-16 px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold tracking-widest uppercase mb-8"
          >
            <SafeIcon icon={FiGift} /> Lifetime Deal — Early Users Only
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-orange-200 to-amber-50 mb-6 leading-tight"
          >
            Own your social proof.<br />Pay for it once.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Moment of Truth pulls your real reviews from Google, YouTube, Trustpilot and Clutch —
            verified, deduplicated, and beautifully published on a wall your prospects actually believe.
            Lifetime access. One payment. Every future update of the plan.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="flex items-baseline gap-3">
              <span className="text-2xl text-zinc-600 line-through font-semibold">$199</span>
              <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 to-orange-300">$149</span>
              <span className="text-zinc-500 font-medium">once. forever.</span>
            </div>

            {alreadyLifetime ? (
              <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-semibold">
                <SafeIcon icon={FiCheckCircle} /> Lifetime access active on your account
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Button variant="primary" onClick={handleBuy} disabled={buying} className="!px-8 !py-4 text-base shadow-[0_0_30px_rgba(234,88,12,0.3)]">
                  {buying ? 'Starting checkout…' : <>Get Lifetime Access <SafeIcon icon={FiArrowRight} /></>}
                </Button>
                <button onClick={scrollToRedeem} className="text-sm font-medium text-zinc-400 hover:text-amber-100 transition-colors px-4 py-2">
                  Have an AppSumo code? <span className="text-orange-400 font-semibold">Redeem it →</span>
                </button>
              </div>
            )}
            <p className="text-xs text-zinc-600">25% launch discount, already applied at checkout. Price rises when we leave beta.</p>
          </motion.div>
        </div>
      </section>

      {/* ---------- DEMO VIDEO ---------- */}
      <section className="px-6 pb-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          <MediaPlaceholder label="Placeholder — product demo video (16:9)" icon={FiPlay} />
        </div>
      </section>

      {/* ---------- WHAT YOU GET ---------- */}
      <section className="py-20 px-6 bg-zinc-900/20 border-y border-orange-500/10 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-sm font-bold tracking-widest text-orange-500 uppercase mb-3">What's inside</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-amber-50 tracking-tight">Everything a Wall of Love needs. For life.</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiZap} title="Every source, synced" desc="Google, YouTube, Trustpilot and Clutch reviews imported automatically — with the original publish dates, ratings and reviewer details." />
            <FeatureCard icon={FiShield} title="Verified, not vibes" desc="We only import reviews we can prove belong to your business. Your wall carries verification badges your prospects can trust." />
            <FeatureCard icon={FiStar} title="Unlimited published reviews" desc="No 25-review cap. Publish every review you've ever earned, from every platform, on one page." />
            <FeatureCard icon={FiLayout} title="All 11 wall layouts" desc="Masonry, timelines, animated walls, marquees, chat streams, magazine spreads — switch anytime, no rebuild." />
            <FeatureCard icon={FiCode} title="Unlimited widgets" desc="Embed testimonial widgets on every page of your site — pricing pages, landing pages, case studies. No limits." />
            <FeatureCard icon={FiAward} title="No Moment of Truth branding" desc="Your wall and widgets, your brand only. The 'Powered by' footer disappears from everything, forever." />
          </div>

          <p className="text-center text-sm text-zinc-500 mt-10 max-w-2xl mx-auto">
            Honest fine print: the lifetime plan doesn't include Pro-only extras
            (custom domain, auto-sync, analytics — rolling out to Pro subscribers).
            You can upgrade to Pro anytime if you need them.
          </p>
        </div>
      </section>

      {/* ---------- SCREENSHOTS ---------- */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <MediaPlaceholder label="Placeholder — Wall of Love screenshot" aspect="aspect-[4/3]" icon={FiImage} />
          <MediaPlaceholder label="Placeholder — widget on customer site" aspect="aspect-[4/3]" icon={FiImage} />
        </div>
      </section>

      {/* ---------- COMPARISON ---------- */}
      <section className="py-20 px-6 bg-zinc-900/20 border-y border-orange-500/10 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-amber-50 tracking-tight">Where Lifetime sits</h3>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-white/5 bg-zinc-900/30 p-6">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left text-xs uppercase tracking-wider text-zinc-500 pb-4"></th>
                  <th className="text-center text-xs uppercase tracking-wider text-zinc-500 pb-4 px-3">Free</th>
                  <th className="text-center text-xs uppercase tracking-wider text-orange-400 pb-4 px-3">Lifetime · $149</th>
                  <th className="text-center text-xs uppercase tracking-wider text-zinc-500 pb-4 px-3">Pro · $21/mo</th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow label="Published reviews" free="25" ltd="Unlimited" pro="Unlimited" />
                <ComparisonRow label="Review sources synced" free="All" ltd="All" pro="All" />
                <ComparisonRow label="Wall layouts" free="2" ltd="All 11" pro="All 11" />
                <ComparisonRow label="Embeddable widgets" free="1" ltd="Unlimited" pro="Unlimited" />
                <ComparisonRow label="Remove our branding" free={false} ltd={true} pro={true} />
                <ComparisonRow label="Priority support" free={false} ltd={true} pro={true} />
                <ComparisonRow label="Custom domain, auto-sync, analytics" free={false} ltd={false} pro="Rolling out" />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ---------- REDEEM ---------- */}
      <section id="redeem" className="py-24 px-6 relative z-10">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-6">
            <SafeIcon icon={FiGift} className="text-2xl text-orange-400" />
          </div>
          <h3 className="text-3xl font-bold text-amber-50 mb-3 tracking-tight">Redeem your code</h3>
          <p className="text-zinc-400 mb-8">
            Got a code from AppSumo or one of our partners? Drop it below.
            {!user && <span className="block mt-1 text-sm text-zinc-500">You'll be asked to sign in (or create a free account) first — we'll bring you right back.</span>}
          </p>

          {alreadyLifetime ? (
            <div className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 font-semibold">
              <SafeIcon icon={FiCheckCircle} /> Lifetime access is already active on this account
            </div>
          ) : redeemState?.ok ? (
            <div className="p-6 rounded-2xl bg-green-500/10 border border-green-500/30">
              <div className="flex items-center justify-center gap-2 text-green-400 font-semibold text-lg mb-3">
                <SafeIcon icon={FiCheckCircle} /> {redeemState.message}
              </div>
              <Button variant="primary" onClick={() => navigate('/dashboard')} className="mt-2">
                Go to your dashboard <SafeIcon icon={FiArrowRight} />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                  placeholder="MOT-XXXXXXXXXX"
                  className="flex-1 bg-[#0a0500]/70 border border-orange-500/20 rounded-xl px-5 py-4 text-center sm:text-left text-amber-100 placeholder-zinc-600 font-mono tracking-widest uppercase focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none"
                />
                <Button variant="primary" onClick={handleRedeem} disabled={redeeming} className="!px-8 !py-4">
                  {redeeming ? 'Redeeming…' : 'Redeem'}
                </Button>
              </div>
              {redeemState && !redeemState.ok && (
                <p className="text-sm text-red-400">{redeemState.message}</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="py-20 px-6 bg-zinc-900/20 border-y border-orange-500/10 relative z-10">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold text-amber-50 mb-10 text-center tracking-tight">Questions, answered honestly</h3>
          <div className="space-y-3">
            <FaqItem
              q="Is it really lifetime?"
              a="Yes. One payment buys the lifetime plan's features for the life of the product, on one account — no renewal, no expiry date, and every future improvement to the plan included."
            />
            <FaqItem
              q="What exactly is NOT included?"
              a="Three Pro-only extras: custom domain hosting for your wall, scheduled auto-sync, and analytics (all rolling out to Pro subscribers). Everything else — unlimited published reviews, all layouts, unlimited widgets, branding removal, priority support — is yours for life. If you later need the Pro extras, you can upgrade anytime."
            />
            <FaqItem
              q="How do AppSumo codes work?"
              a="Buy on AppSumo, get a code, paste it in the box above. You'll sign in (or create a free account), the code unlocks lifetime access instantly, and your reviews start syncing right away. One code per account."
            />
            <FaqItem
              q="What about refunds?"
              a="Codes purchased through a marketplace like AppSumo follow that marketplace's refund policy. Bought directly from this page and having second thoughts? Email us and we'll sort you out — we'd rather have your trust than your money."
            />
            <FaqItem
              q="Why so cheap if it's so good?"
              a="We're early, and early users take a chance on us. In return you lock in the lowest price this product will ever have, and we get people who'll tell us the truth about what to build next. That's the trade."
            />
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="py-24 px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-3xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">
            Your reviews already exist.<br />Put them to work — forever.
          </h3>
          {!alreadyLifetime && (
            <div className="flex flex-col items-center gap-3 mt-8">
              <Button variant="primary" onClick={handleBuy} disabled={buying} className="!px-10 !py-4 text-base shadow-[0_0_30px_rgba(234,88,12,0.3)]">
                {buying ? 'Starting checkout…' : <>Get Lifetime Access — $149 <SafeIcon icon={FiArrowRight} /></>}
              </Button>
              <button onClick={scrollToRedeem} className="text-sm text-zinc-500 hover:text-amber-100 transition-colors">
                or redeem an AppSumo code
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LifetimeDeal;
